import React from 'react';
import { VonChatFilters } from '../types';
import { PrecisionSearchInput } from './PrecisionSearchInput';

interface NeuralSearchToggleProps {
  filters: VonChatFilters;
  onFiltersChange: (filters: Partial<VonChatFilters>) => void;
}

export const NeuralSearchToggle: React.FC<NeuralSearchToggleProps> = ({
  filters,
  onFiltersChange
}) => {
  const handleToggle = () => {
    onFiltersChange({
      useNeuralSearch: !filters.useNeuralSearch
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Neural Search</label>
        <div className="flex items-center space-x-2">
          <span className={`text-xs ${filters.useNeuralSearch ? 'text-green-600' : 'text-gray-500'}`}>
            {filters.useNeuralSearch ? 'ON' : 'OFF'}
          </span>
          <button
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              filters.useNeuralSearch ? 'bg-green-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                filters.useNeuralSearch ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className={`p-3 rounded-lg border ${
        filters.useNeuralSearch 
          ? 'bg-green-50 border-green-200' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-start space-x-2">
          <div className={`mt-0.5 w-2 h-2 rounded-full ${
            filters.useNeuralSearch ? 'bg-green-500' : 'bg-gray-400'
          }`}></div>
          <div className="flex-1">
            <div className={`text-sm font-medium ${
              filters.useNeuralSearch ? 'text-green-800' : 'text-gray-700'
            }`}>
              {filters.useNeuralSearch ? 'Neural Search Enabled' : 'Neural Search Disabled'}
            </div>
            <div className={`text-xs mt-1 ${
              filters.useNeuralSearch ? 'text-green-700' : 'text-gray-600'
            }`}>
              {filters.useNeuralSearch 
                ? 'AI will search through your data using semantic understanding to find relevant context for better responses.'
                : 'AI will respond based on general knowledge without searching your specific data.'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Precision Search - Only show when Neural Search is enabled */}
      {filters.useNeuralSearch && (
        <div className="border-t border-gray-200 pt-4">
          <PrecisionSearchInput 
            filters={filters} 
            onFiltersChange={onFiltersChange} 
          />
        </div>
      )}

      {/* Additional Info */}
      <div className="text-xs text-gray-500 space-y-1">
        <div className="flex items-center space-x-2">
          <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
          <span>Neural search analyzes transcript content semantically</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
          <span>Works with date and client filters for targeted results</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
          <span>May increase response time but improves accuracy</span>
        </div>
        {filters.useNeuralSearch && filters.precisionSearchQuery && (
          <div className="flex items-center space-x-2">
            <span className="w-1 h-1 bg-purple-400 rounded-full"></span>
            <span>Precision Mode active - using custom search terms</span>
          </div>
        )}
      </div>
    </div>
  );
}; 